import React, { Fragment, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Empty, Skeleton, Switch } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { pgProviderRouteQuery } from '../../queries';
import { useUpdatePgProviderRoutesMutation } from '../../hooks/usePgProviderRouteMutations';
import { useSettingsDraft } from '../../hooks/useSettingsDraft';
import { SettingsPanelProps } from '../../types';
import { SettingsSection } from '../../common/SettingsSection';
import { SettingsSaveBar } from '../../common/SettingsSaveBar';
import { SettingsReviewModal } from '../../common/SettingsReviewModal';
import { SortableSettingRow } from '../../common/SortableSettingRow';
import { getRouteChanges, moveItem, toEditableRoutes, toRoutePayload } from '../../utils/settingsChanges';
import { DEFAULT_PG_ENG_NAME_BY_PAY_METHOD_TYPE, getAccountTypeLabel } from '../../constants/labels';
import { extractErrorMessage } from '../../../../lib/utils/checkErrorResponse';
import { RouteSimulator } from '../RouteSimulator/RouteSimulator';
import { RouteEditor } from './RouteEditor';
import { RouteListStyle } from './styles';

export const RoutePanel = (props: SettingsPanelProps): React.JSX.Element => {
  const { canUpdate, onDirtyChange } = props;
  const query = useQuery(pgProviderRouteQuery.list());
  const draft = useSettingsDraft(query.data ? toEditableRoutes(query.data.routes) : undefined);
  const mutation = useUpdatePgProviderRoutesMutation();
  const { message } = App.useApp();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isReviewOpen, setReviewOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => onDirtyChange(draft.isDirty), [draft.isDirty, onDirtyChange]);

  if (query.isError)
    return (
      <Alert
        type="error"
        showIcon
        message="PG 설정을 불러오지 못했습니다."
        action={<Button onClick={() => query.refetch()}>다시 시도</Button>}
      />
    );
  if (!query.data || !draft.value || !draft.baseline) return <Skeleton active paragraph={{ rows: 7 }} />;
  const { master } = query.data;
  const routes = draft.value;
  const disabled = !canUpdate || mutation.isLoading;
  const changes = getRouteChanges(draft.baseline, routes, master);
  const isInvalid = routes.some(
    (route) =>
      !master.payMethods.some((item) => item.id === route.payMethodId) ||
      !master.pgProviders.some((item) => item.id === route.pgProviderId) ||
      (route.maxAmount !== null && (!Number.isInteger(route.maxAmount) || route.maxAmount <= 0)) ||
      (route.accountType ? [route.accountType] : ['general', 'liquidity']).some(
        (type) =>
          !master.pgProviderAccounts.some(
            (account) => account.pgProviderId === route.pgProviderId && account.accountType === type,
          ),
      ),
  );

  const handleAdd = (): void => {
    const rowKey = 'new-' + crypto.randomUUID();
    draft.update((current) => [
      ...current,
      {
        rowKey,
        payMethodId: master.payMethods.find((item) => item.type === 'card')?.id ?? master.payMethods[0]?.id ?? 0,
        pgProviderId: master.pgProviders.find((item) => item.engName === 'nice')?.id ?? master.pgProviders[0]?.id ?? 0,
        accountType: null,
        maxAmount: null,
        priority: (current.length + 1) * 10,
        isActive: true,
        memo: null,
      },
    ]);
    setEditingKey(rowKey);
  };
  /**
   * 🔴 저장 직전에 한 번 더 읽어 다른 사람의 변경을 확인한다.
   *
   * 이 화면은 열어 둔 뒤 자동으로 다시 조회하는 경로가 없다 — 전역 `refetchOnWindowFocus` 가
   * 꺼져 있고 탭은 `forceRender` 라 재마운트되지 않는다. 그래서 확인 없이 저장하면
   * 그 사이 다른 관리자가 바꾼 내용을 모른 채 덮어쓴다(서버 잠금은 동시에 들어온 저장을
   * 줄 세울 뿐, 오래된 기준값으로 온 요청은 막지 못한다).
   */
  const handleReview = async (): Promise<void> => {
    setSaveError(null);
    const { data } = await query.refetch();

    if (draft.isOutdated(data && toEditableRoutes(data.routes))) {
      message.warning('다른 관리자가 설정을 바꿨습니다. 변경 취소 후 최신 설정으로 다시 편집해 주세요.');

      return;
    }
    setReviewOpen(true);
  };
  const handleSave = async (): Promise<void> => {
    if (!canUpdate || mutation.isLoading || draft.hasRemoteChanges || isInvalid) return;
    setSaveError(null);
    try {
      await mutation.mutateAsync({ routes: toRoutePayload(routes) });
      draft.reset();
      setReviewOpen(false);
      setEditingKey(null);
      message.success('PG 라우팅 설정을 저장했습니다.');
    } catch (error) {
      setSaveError(error instanceof Error ? extractErrorMessage(error) : '저장하지 못했습니다. 다시 시도해 주세요.');
    }
  };
  return (
    <>
      <div className="settings-panel-header">
        <div>
          <h2>결제 요청을 연결할 PG</h2>
          <p>위에서부터 처음 일치하는 활성 규칙을 적용합니다.</p>
        </div>
        <Button icon={<PlusOutlined />} disabled={disabled} onClick={handleAdd}>
          규칙 추가
        </Button>
      </div>
      {draft.hasRemoteChanges && (
        <Alert
          className="settings-inline-alert"
          type="warning"
          showIcon
          message="다른 변경 사항이 조회되었습니다. 변경 취소 후 최신 설정으로 다시 편집해 주세요."
        />
      )}
      <div className="settings-two-columns">
        <SettingsSection
          title="적용 규칙"
          action={<span className="settings-muted">{routes.filter((route) => route.isActive).length}개 사용</span>}
        >
          <RouteListStyle>
            {routes.length === 0 && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="등록된 규칙이 없습니다. 기본 연결을 사용합니다."
              />
            )}
            {routes.map((route, index) => (
              <Fragment key={route.rowKey}>
                <SortableSettingRow
                  id={route.rowKey}
                  group="payment-routing"
                  index={index}
                  count={routes.length}
                  label={'규칙 ' + (index + 1)}
                  disabled={disabled}
                  isSelected={editingKey === route.rowKey}
                  onMove={(from, to) => draft.update((current) => moveItem(current, from, to))}
                >
                  <div className="rule-summary">
                    <div>
                      <div className="rule-title">
                        <span className="rule-order">{index + 1}</span>
                        {master.payMethods.find((item) => item.id === route.payMethodId)?.name}
                      </div>
                      <div className="rule-condition">
                        {getAccountTypeLabel(route.accountType)} 계정 ·{' '}
                        {route.maxAmount === null
                          ? '금액 제한 없음'
                          : route.maxAmount.toLocaleString('ko-KR') + '원 이하'}
                      </div>
                      <div className="rule-target">
                        → {master.pgProviders.find((item) => item.id === route.pgProviderId)?.korName}
                      </div>
                    </div>
                    <div className="rule-actions">
                      <div className="rule-status">
                        <Switch
                          aria-label={'규칙 ' + (index + 1) + ' 사용'}
                          checked={route.isActive}
                          disabled={disabled}
                          onChange={(checked) =>
                            draft.update((current) =>
                              current.map((item) =>
                                item.rowKey === route.rowKey ? { ...item, isActive: checked } : item,
                              ),
                            )
                          }
                        />
                        <span>{route.isActive ? '사용' : '미사용'}</span>
                      </div>
                      <Button
                        size="small"
                        onClick={() => setEditingKey(editingKey === route.rowKey ? null : route.rowKey)}
                      >
                        {editingKey === route.rowKey ? '닫기' : canUpdate ? '편집' : '상세'}
                      </Button>
                    </div>
                  </div>
                </SortableSettingRow>
                {editingKey === route.rowKey && (
                  <RouteEditor
                    route={route}
                    master={master}
                    disabled={disabled}
                    onChange={(patch) =>
                      draft.update((current) =>
                        current.map((item) => (item.rowKey === route.rowKey ? { ...item, ...patch } : item)),
                      )
                    }
                    onRemove={() => {
                      draft.update((current) => current.filter((item) => item.rowKey !== route.rowKey));
                      setEditingKey(null);
                    }}
                    onClose={() => setEditingKey(null)}
                  />
                )}
              </Fragment>
            ))}
            <div className="rule-default">
              <details>
                <summary>일치하는 규칙이 없을 때의 기본 연결</summary>
                <dl>
                  {master.payMethods.map((method) => (
                    <Fragment key={method.id}>
                      <dt>{method.name}</dt>
                      <dd>
                        {master.pgProviders.find(
                          (pg) => pg.engName === DEFAULT_PG_ENG_NAME_BY_PAY_METHOD_TYPE[method.type],
                        )?.korName ?? '기본 연결 없음'}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
              </details>
            </div>
          </RouteListStyle>
        </SettingsSection>
        <RouteSimulator routes={toRoutePayload(routes)} master={master} isDirty={draft.isDirty} />
      </div>
      {isInvalid && (
        <Alert
          className="settings-inline-alert"
          type="error"
          showIcon
          message="규칙의 금액·PG·계정 정보를 확인해 주세요. 잘못된 설정은 저장할 수 없습니다."
        />
      )}
      <SettingsSaveBar
        isDirty={draft.isDirty}
        isSaving={mutation.isLoading}
        canUpdate={canUpdate}
        disabled={draft.hasRemoteChanges || isInvalid || changes.length === 0}
        onReset={() => {
          draft.reset();
          setEditingKey(null);
        }}
        onReview={handleReview}
      />
      <SettingsReviewModal
        open={isReviewOpen}
        title="PG 라우팅 변경 검토"
        changes={changes}
        isSaving={mutation.isLoading}
        error={saveError}
        onSave={handleSave}
        onClose={() => setReviewOpen(false)}
      />
    </>
  );
};
